import { useMemo } from 'react'

import { MenuGroup } from './types'

/**
 * Recursively searches through menu groups to find a category by slug
 */
const findCategoryBySlug = (
  groups: MenuGroup[],
  slug: string
): MenuGroup | null => {
  for (const group of groups) {
    if (group.slug === slug) {
      return group
    }
    if (group.children && group.children.length > 0) {
      const found = findCategoryBySlug(group.children, slug)
      if (found) return found
    }
  }
  return null
}

/**
 * Represents the path from root to a category
 */
export type CategoryPath = {
  /** Top-level parent group (or the category itself if it's at root level) */
  parent: MenuGroup | null
  /** Second-level group (if category is at level 2 or deeper) */
  child: MenuGroup | null
  /** The category itself */
  category: MenuGroup | null
}

/**
 * Recursively searches through menu groups to find a category and its path
 * @returns The path (ancestors) to the category, or null if not found
 */
const findCategoryPath = (
  groups: MenuGroup[],
  slug: string,
  ancestors: MenuGroup[] = []
): CategoryPath | null => {
  for (const group of groups) {
    if (group.slug === slug) {
      // Found the category - build the path
      return {
        parent: ancestors[0] ?? null,
        child: ancestors[1] ?? (ancestors.length === 0 ? null : group),
        category: group
      }
    }
    if (group.children && group.children.length > 0) {
      const found = findCategoryPath(group.children, slug, [
        ...ancestors,
        group
      ])
      if (found) return found
    }
  }
  return null
}

/**
 * Hook to find a menu category/group by its slug
 * @param categorySlug - The slug to search for
 * @param menuGroups - The menu groups to search through
 * @returns The menu group matching the slug, or null if not found
 */
export const useCategoryFromSlug = (
  categorySlug: string | undefined,
  menuGroups: MenuGroup[] | undefined | null
): MenuGroup | null => {
  return useMemo(() => {
    if (!categorySlug || !menuGroups) {
      return null
    }

    return findCategoryBySlug(menuGroups, categorySlug)
  }, [categorySlug, menuGroups])
}

/**
 * Hook to find a category's path (ancestors) by its slug
 * Useful for pre-selecting parent groups in navigation
 * @param categorySlug - The slug to search for
 * @param menuGroups - The menu groups to search through
 * @returns The path to the category including parent and child ancestors
 */
export const useCategoryPath = (
  categorySlug: string | undefined,
  menuGroups: MenuGroup[] | undefined | null
): CategoryPath | null => {
  return useMemo(() => {
    if (!categorySlug || !menuGroups) {
      return null
    }

    return findCategoryPath(menuGroups, categorySlug)
  }, [categorySlug, menuGroups])
}
